
import { clamp, last } from 'lodash';
import { mapState, mapGetters } from 'vuex';
import {
  FLING_VELOCITY_THRESHOLD,
  FLING_VELOCITY_WINDOW_MS
} from '@/constants/GESTURE';
import { reviewAskOrigin } from '@/lib/ReviewAskApi';

const MESSAGE_NS = 'crema_review_ask';
const READY_TIMEOUT_MS = 5000;
const SHEET_VH = 92;
const SHEET_CLOSE_THRESHOLD_RATIO = 0.3;
const MOBILE_MAX_WIDTH = 720;
const PANEL_QUERY_KEYS = [
  'product_id',
  'question',
  'locale',
  'main_color',
  'presets',
  'page_view_id'
];

export default {
  name: 'ReviewAskDialog',
  data() {
    return {
      isReady: false,
      isFailed: false,
      hasRetried: false,
      // 위젯 자리 너비가 아니라 쇼핑몰 화면 너비로 판정한다 — 로더가 이 팝업 iframe을 폭 100%로 띄운다.
      isMobile: window.innerWidth < MOBILE_MAX_WIDTH,
      reloadKey: 0,
      isDragging: false,
      dragHeight: null
    };
  },
  computed: {
    ...mapState('window', ['windowService']),
    ...mapState('session', ['sessionParams']),
    ...mapGetters(['cremaDomainPrefix']),
    askOrigin() {
      return reviewAskOrigin(this.cremaDomainPrefix);
    },
    panelUrl() {
      // 로더가 wrapper URL에 자동 부착하는 파라미터(iframe_id 등)는 패널에 전달하지 않는다.
      const params = new URLSearchParams();
      params.set('brand_code', this.$route.params.brand_code);
      PANEL_QUERY_KEYS.forEach(key => {
        const value = this.$route.query[key];
        if (value != null && value !== '') params.set(key, value);
      });
      // 로더가 팝업 URL에도 붙여주는 값이라 위젯 쪽과 같은 값이다.
      const secureDeviceToken = this.sessionParams.secure_device_token;
      if (secureDeviceToken)
        params.set('secure_device_token', secureDeviceToken);
      if (this.reloadKey) params.set('retry', this.reloadKey);
      return `${this.askOrigin}/panel?${params.toString()}`;
    },
    panelStyle() {
      if (!this.isMobile) return null;
      if (this.isDragging)
        return { height: `${this.dragHeight}px`, transition: 'none' };
      return { height: `${SHEET_VH}vh` };
    }
  },
  created() {
    this.readyTimer = null;
    this.dragPointerId = null;
    this.dragStartY = null;
    this.dragStartHeight = null;
    this.dragMaxHeight = null;
    this.closeThreshold = null;
    this.recentDragPoints = null;

    window.addEventListener('message', this.onMessage);
    document.addEventListener('keydown', this.onKeydown);
    this.startReadyTimer();
  },
  beforeDestroy() {
    window.removeEventListener('message', this.onMessage);
    document.removeEventListener('keydown', this.onKeydown);
    this.clearReadyTimer();
  },
  methods: {
    startReadyTimer() {
      this.clearReadyTimer();
      this.readyTimer = setTimeout(() => {
        if (!this.isReady) this.isFailed = true;
      }, READY_TIMEOUT_MS);
    },
    clearReadyTimer() {
      if (this.readyTimer) clearTimeout(this.readyTimer);
      this.readyTimer = null;
    },
    onMessage(event) {
      if (event.origin !== this.askOrigin) return;
      const data = event.data;
      if (!data || data.ns !== MESSAGE_NS) return;

      if (data.act === 'ready') {
        this.isReady = true;
        this.isFailed = false;
        this.clearReadyTimer();
      } else if (data.act === 'close') {
        this.windowService.closeFullscreenPopup(data.response || '');
      }
    },
    onKeydown(event) {
      if (event.key !== 'Escape') return;

      // 패널이 열린 뒤의 ESC는 패널이 자체 처리한다 — wrapper 감지는 로딩·에러 상태용 보조 경로.
      if (this.isReady) {
        this.$refs.panelIframe?.contentWindow?.postMessage(
          { ns: MESSAGE_NS, act: 'esc' },
          this.askOrigin
        );
      } else {
        this.close();
      }
    },
    sheetVhToPx(vh) {
      return (window.innerHeight * vh) / 100;
    },
    isActiveDragPointer(event) {
      return event.pointerId === this.dragPointerId;
    },
    onDragStart(event) {
      if (this.isDragging) return;

      event.target.setPointerCapture(event.pointerId);

      const sheetHeight = this.sheetVhToPx(SHEET_VH);

      this.dragPointerId = event.pointerId;
      this.dragStartY = event.clientY;
      this.recentDragPoints = [{ y: event.clientY, time: event.timeStamp }];
      // 스냅 애니메이션 도중 다시 잡으면 상태상 높이가 아닌 화면에 보이는 높이에서 이어간다.
      this.dragStartHeight = this.$refs.panel.offsetHeight;
      this.dragMaxHeight = sheetHeight;
      this.closeThreshold =
        sheetHeight - sheetHeight * SHEET_CLOSE_THRESHOLD_RATIO;
      this.isDragging = true;
      this.dragHeight = this.dragStartHeight;
    },
    onDragMove(event) {
      if (!this.isActiveDragPointer(event)) return;
      this.recordDragPoint(event);
    },
    recordDragPoint(event) {
      this.recentDragPoints.push({ y: event.clientY, time: event.timeStamp });
      while (
        event.timeStamp - this.recentDragPoints[0].time >
        FLING_VELOCITY_WINDOW_MS
      )
        this.recentDragPoints.shift();

      const draggedUpBy = this.dragStartY - event.clientY;
      this.dragHeight = clamp(
        this.dragStartHeight + draggedUpBy,
        0,
        this.dragMaxHeight
      );
    },
    // 릴리스 기준 최근 FLING_VELOCITY_WINDOW_MS 이력의 평균 속도(px/ms, 아래로 양수).
    getDragVelocity() {
      const oldest = this.recentDragPoints[0];
      const latest = last(this.recentDragPoints);

      const timeDelta = latest.time - oldest.time;
      // 창 안에 표본이 하나뿐이면(멈췄다 놓음, 뭉친 timeStamp) 위치 판정에 맡긴다.
      if (timeDelta <= 0) return 0;

      return (latest.y - oldest.y) / timeDelta;
    },
    onDragEnd(event) {
      if (!this.isActiveDragPointer(event)) return;

      // 마지막 move 이후의 이동은 pointerup 좌표에만 실려 오므로 릴리스 지점도 표본에 반영한다.
      this.recordDragPoint(event);
      const releasedHeight = this.dragHeight;
      const velocityDown = this.getDragVelocity();
      this.resetDrag();

      // 위로 튕기면 놓은 위치와 상관없이 닫지 않겠다는 의사로 보고 시트 높이로 돌아간다.
      if (velocityDown < -FLING_VELOCITY_THRESHOLD) return;

      if (
        velocityDown > FLING_VELOCITY_THRESHOLD ||
        releasedHeight <= this.closeThreshold
      )
        this.close();
    },
    // OS가 포인터를 가로챈 경우(전화 수신·회전 등)는 닫기 판정 없이 원상 복귀한다.
    onDragCancel(event) {
      if (!this.isActiveDragPointer(event)) return;
      this.resetDrag();
    },
    // 나머지 드래그 필드는 onDragStart가 읽기 전에 전부 재시딩하므로 여기서 다루지 않는다.
    resetDrag() {
      this.isDragging = false;
      this.dragHeight = null;
      this.dragPointerId = null;
    },
    retry() {
      this.hasRetried = true;
      this.isFailed = false;
      this.reloadKey += 1;
      this.startReadyTimer();
    },
    close() {
      this.windowService.closeFullscreenPopup();
    }
  }
};
